import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


class ExperienceCheckoutService {
  /**
   * Create a Stripe PaymentIntent for experience bookings
   * @param {Array} experienceItems - Array of experience cart items
   * @param {Object} billingAddress - Billing address details
   * @param {Object} selectedClients - Map of cart item IDs to client details
   * @returns {Promise<Object>} PaymentIntent client secret and details
   */
  async createPaymentIntent(experienceItems, billingAddress, selectedClients, paymentMethodId) {
    try {
      const response = await api.post('/experience-checkout/create-payment-intent', {
        experienceItems,
        billingAddress,
        selectedClients,
        ...(paymentMethodId && { paymentMethodId })
      });
      return response.data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to create payment intent. Please try again.'
      );
    }
  }

  /**
   * One Stripe PaymentIntent per experience cart line (supports mixed currencies).
   */
  async createPaymentIntentsPerLine(experienceItems, billingAddress, selectedClients, paymentMethodId, qaStripeMinPromoCode) {
    try {
      const response = await api.post('/experience-checkout/create-payment-intents', {
        experienceItems,
        billingAddress,
        selectedClients,
        ...(paymentMethodId && { paymentMethodId }),
        ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Error creating payment intents:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to create payment intents. Please try again.'
      );
    }
  }

  async getStripePaymentMethods() {
    try {
      const response = await api.get('/experience-checkout/payment-methods');
      return response.data;
    } catch (error) {
      console.error('Error fetching Stripe payment methods:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to load payment methods.'
      );
    }
  }

  async createSetupIntent() {
    try {
      const response = await api.post('/experience-checkout/setup-intent');
      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to add card. Please try again.'
      );
    }
  }

  async saveStripePaymentMethod(paymentMethodId) {
    try {
      const response = await api.post('/experience-checkout/payment-methods', {
        paymentMethodId
      });
      return response.data;
    } catch (error) {
      console.error('Error saving payment method:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to save card.'
      );
    }
  }

  async removeStripePaymentMethod(id) {
    try {
      const response = await api.delete(`/experience-checkout/payment-methods/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error removing payment method:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to remove card.'
      );
    }
  }

  /**
   * Confirm booking after successful payment
   * @param {String} paymentIntentId - Stripe PaymentIntent ID
   * @param {Array} experienceItems - Array of experience cart items
   * @param {Object} selectedClients - Map of cart item IDs to client details
   * @param {Object} billingAddress - Billing address details
   * @returns {Promise<Object>} Created bookings
   */
  async confirmBooking(paymentIntentId, experienceItems, selectedClients, billingAddress, qaStripeMinPromoCode) {
    try {
      const response = await api.post('/experience-checkout/confirm-booking', {
        paymentIntentId,
        experienceItems,
        selectedClients,
        billingAddress,
        ...(qaStripeMinPromoCode ? { qaStripeMinPromoCode } : {})
      });
      return response.data;
    } catch (error) {
      console.error('Error confirming booking:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to confirm booking. Please contact support.'
      );
    }
  }

  /**
   * Get booking details by reference
   * @param {String} bookingReference - Booking reference number
   * @returns {Promise<Object>} Booking details
   */
  async getBooking(bookingReference) {
    try {
      const response = await api.get(`/experience-checkout/booking/${bookingReference}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching booking:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to fetch booking details.'
      );
    }
  }

  /**
   * Cancel booking and process refund
   * @param {String} bookingReference - Booking reference number
   * @param {String} reason - Cancellation reason
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelBooking(bookingReference, reason) {
    try {
      const response = await api.post(`/experience-checkout/cancel-booking/${bookingReference}`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw new Error(
        error.response?.data?.message || 
        'Failed to cancel booking. Please try again.'
      );
    }
  }
}

export default new ExperienceCheckoutService();
